import { z } from 'zod'

// Scheduled maintenance against the flight log's airframe hours, ported
// from mik-ng's aircraft maintenance block and its green/amber/red bar.
// The fleet manager, CAMO or maintenance staff keep the schedule and the
// records in the admin app; every member sees how many hours are left on
// the Fleet page. Pure, so the browser draws the bar with the same rule the
// API serves.

// Each aircraft's next maintenance (flight.maintenance_schedules).
export interface MaintenanceSchedule {
  registration: string
  // The interval "maintenance performed" moves the next due point by.
  cycleHours: number
  // What is due next: "100h", "200h", "Annual"...
  nextType: string
  // The airframe total, in minutes, it is due at.
  nextDueMinutes: number
  // A calendar limit as well, YYYY-MM-DD, or null.
  nextDueDate: string | null
  // How far past the due point the aircraft may still fly, and the last of
  // those hours kept back (the ferry flight to maintenance).
  toleranceHours: number
  reservedHours: number
  notes: string | null
  updatedAt: string
  updatedByName: string | null
}

// Maintenance performed (flight.maintenance_records).
export interface MaintenanceRecord {
  recordId: number
  registration: string
  performedOn: string
  maintenanceType: string
  airframeMinutes: number
  performedBy: string | null
  notes: string | null
  recordedByName: string | null
  createdAt: string
}

// green  before the due point
// amber  past it, inside the tolerance the members may still use
// red    in the reserved hours: only the flight to maintenance
// over   past the hard limit (due + tolerance), or past the due date
export type MaintenanceZone = 'green' | 'amber' | 'red' | 'over'

export interface MaintenanceStatus {
  // Airframe minutes until the due point; negative once past it.
  minutesToDue: number
  // What members may still plan to fly: to the due point, plus the
  // tolerance, less the reserved hours. Negative when none is left.
  usableMinutes: number
  // Calendar days until next_due_date (0 on the day), or null.
  daysToDue: number | null
  zone: MaintenanceZone
}

const MINUTES_PER_HOUR = 60

// Whole days from `today` to `date`, both YYYY-MM-DD.
const daysBetween = (today: string, date: string) =>
  Math.round((Date.parse(`${date}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000)

export function maintenanceStatus(
  schedule: Pick<MaintenanceSchedule, 'nextDueMinutes' | 'nextDueDate' | 'toleranceHours' | 'reservedHours'>,
  airframeMinutes: number,
  today: string,
): MaintenanceStatus {
  const minutesToDue = schedule.nextDueMinutes - airframeMinutes
  const toleranceMinutes = schedule.toleranceHours * MINUTES_PER_HOUR
  const reservedMinutes = schedule.reservedHours * MINUTES_PER_HOUR
  const usableMinutes = minutesToDue + toleranceMinutes - reservedMinutes
  const daysToDue = schedule.nextDueDate ? daysBetween(today, schedule.nextDueDate) : null

  // The hard limit is due + tolerance; the reserved hours are its last part.
  const zone: MaintenanceZone =
    minutesToDue + toleranceMinutes <= 0 || (daysToDue !== null && daysToDue < 0)
      ? 'over'
      : usableMinutes <= 0
        ? 'red'
        : minutesToDue <= 0
          ? 'amber'
          : 'green'
  return { minutesToDue, usableMinutes, daysToDue, zone }
}

// The bar, as hours on one axis running from the hard limit (left, -
// tolerance) to a full cycle ahead (right): the red reserved hours, the
// amber tolerance members may use, then green to a whole cycle. `marker`
// is where the aircraft is now, clamped onto the bar.
export interface MaintenanceBarLayout {
  // Percentages of the bar's width, left to right.
  red: number
  amber: number
  green: number
  marker: number
}

export function maintenanceBarLayout(
  schedule: Pick<MaintenanceSchedule, 'cycleHours' | 'toleranceHours' | 'reservedHours'>,
  minutesToDue: number,
): MaintenanceBarLayout {
  const tolerance = schedule.toleranceHours
  const reserved = Math.min(schedule.reservedHours, tolerance)
  const total = tolerance + schedule.cycleHours
  if (total <= 0) return { red: 0, amber: 0, green: 100, marker: 100 }
  const pct = (hours: number) => (100 * hours) / total
  const position = tolerance + minutesToDue / MINUTES_PER_HOUR
  return {
    red: pct(reserved),
    amber: pct(tolerance - reserved),
    green: pct(schedule.cycleHours),
    marker: Math.min(100, Math.max(0, pct(position))),
  }
}

// GET /api/maintenance/schedules: one row per fleet aircraft. `schedule`
// is null until someone sets one up; `airframeMinutes` is null until the
// aircraft's journey log is opened (admin -> Journey logs). `status` needs
// both.
export interface AircraftMaintenance {
  registration: string
  aircraftType: string | null
  airframeMinutes: number | null
  schedule: MaintenanceSchedule | null
  lastRecord: MaintenanceRecord | null
  status: MaintenanceStatus | null
}

// --- Performing maintenance ------------------------------------------------------
// The huolto app's "perform maintenance" screen: pick the maintenance, and the
// next one is filled in from it -- editable, never forced.

// The standard intervals offered as one-tap choices. `cycleHours` is what
// "maintenance performed" adds to set the next due point; an annual is a
// calendar item, so it leaves the hourly cycle as it was (null).
export const MAINTENANCE_TYPES = [
  { label: '50h', cycleHours: 50 },
  { label: '100h', cycleHours: 100 },
  { label: '200h', cycleHours: 200 },
  { label: '400h', cycleHours: 400 },
  { label: 'Annual', cycleHours: null },
] as const

// The cycle a maintenance type implies: "100h" -> 100, "Annual" -> null, and
// anything typed freely -> null, which the caller treats as "keep the
// schedule's current cycle". Case and spacing do not matter.
export function cycleHoursFor(maintenanceType: string): number | null {
  const match = /^\s*(\d{1,4})\s*h\s*$/i.exec(maintenanceType)
  return match ? Number(match[1]) : null
}

// Where the next maintenance falls due: this maintenance's airframe hours
// plus one cycle, in minutes.
export const nextDueAfter = (airframeMinutes: number, cycleHours: number) => airframeMinutes + cycleHours * MINUTES_PER_HOUR

// The next due date an annual sets: a year on, clamped for 29 February.
export function oneYearOn(date: string): string {
  const [y = 0, m = 1, d = 1] = date.split('-').map(Number)
  const lastDay = new Date(Date.UTC(y + 1, m, 0)).getUTCDate()
  return `${y + 1}-${String(m).padStart(2, '0')}-${String(Math.min(d, lastDay)).padStart(2, '0')}`
}

// What the schedule becomes once a maintenance is performed, prefilled for
// the mechanic to confirm or change:
//
//  - the next due hours are this maintenance's airframe hours plus a cycle,
//    the cycle being the type's own ("200h" -> 200) or else the schedule's;
//  - what is due next is that cycle ("100h");
//  - an annual moves the calendar limit a year on, but an hourly maintenance
//    leaves an existing calendar limit exactly where it was -- doing a 100 h
//    must not make the annual's due date disappear;
//  - tolerance, reserve and notes carry over, as the mechanic does not set
//    the club's flying policy (the fleet manager does, in admin).
export function planNextMaintenance(input: {
  maintenanceType: string
  airframeMinutes: number
  performedOn: string
  schedule: Pick<MaintenanceSchedule, 'cycleHours' | 'nextDueDate' | 'toleranceHours' | 'reservedHours' | 'notes'> | null
}): MaintenanceScheduleInput {
  const { schedule } = input
  const cycleHours = cycleHoursFor(input.maintenanceType) ?? schedule?.cycleHours ?? 100
  const isAnnual = input.maintenanceType.trim().toLowerCase() === 'annual'
  return {
    cycleHours,
    nextType: `${cycleHours}h`,
    nextDueMinutes: nextDueAfter(input.airframeMinutes, cycleHours),
    nextDueDate: isAnnual ? oneYearOn(input.performedOn) : (schedule?.nextDueDate ?? null),
    toleranceHours: schedule?.toleranceHours ?? 10,
    reservedHours: schedule?.reservedHours ?? 0,
    notes: schedule?.notes ?? null,
  }
}

// --- Tracked parts -------------------------------------------------------------
// The engine, propeller and hourly-interval equipment (flight.aircraft_components),
// measured on the airframe clock like everything else here.

export const COMPONENT_KINDS = ['engine', 'propeller', 'equipment'] as const
export type ComponentKind = (typeof COMPONENT_KINDS)[number]

export interface AircraftComponent {
  componentId: number
  registration: string
  kind: ComponentKind
  name: string
  // The hourly interval (TBO for an engine), or null when the part is only
  // counted since it was last done.
  intervalHours: number | null
  // The airframe total, in minutes, when it was last overhauled or serviced.
  lastDoneMinutes: number
  lastDoneOn: string | null
  notes: string | null
  // How many airframe hours before it is due the part turns amber and the
  // maintenance staff are mailed (default 50).
  alertBeforeHours: number
  // The interval is a time between overhauls: the part is overhauled or
  // replaced at it, not merely inspected.
  isTbo: boolean
  // Set when the part is archived (soft delete); null while it is tracked.
  archivedAt: string | null
}

export interface ComponentStatus {
  // Airframe minutes since the part was last done.
  minutesSince: number
  // Minutes until its interval is up; negative once past, null without one.
  minutesToDue: number | null
  zone: MaintenanceZone | null
}

// A part inside its own alert window (alertBeforeHours) is amber; at or
// past its interval, red. There is no tolerance on a part: when it is up,
// it is up.
export function componentStatus(
  component: Pick<AircraftComponent, 'intervalHours' | 'lastDoneMinutes' | 'alertBeforeHours'>,
  airframeMinutes: number,
): ComponentStatus {
  const minutesSince = Math.max(0, airframeMinutes - component.lastDoneMinutes)
  if (component.intervalHours == null) return { minutesSince, minutesToDue: null, zone: null }
  const minutesToDue = component.intervalHours * MINUTES_PER_HOUR - minutesSince
  const zone: MaintenanceZone =
    minutesToDue <= 0 ? 'red' : minutesToDue <= component.alertBeforeHours * MINUTES_PER_HOUR ? 'amber' : 'green'
  return { minutesSince, minutesToDue, zone }
}

// A tracked part that is inside its alert window or past due: what the
// daily alert mails and the admin list highlights.
export interface ComponentDueSoon {
  component: AircraftComponent
  // The airframe total, in minutes, the part falls due at. Part of the
  // alert's dedupe key, so a reset (a new lastDoneMinutes) re-arms it.
  dueAtMinutes: number
  minutesToDue: number
  pastDue: boolean
}

export function componentsDueSoon(
  components: readonly AircraftComponent[],
  airframeMinutes: number,
): ComponentDueSoon[] {
  const due: ComponentDueSoon[] = []
  for (const component of components) {
    if (component.archivedAt != null || component.intervalHours == null) continue
    const { minutesToDue, zone } = componentStatus(component, airframeMinutes)
    if (minutesToDue == null || zone === 'green') continue
    due.push({
      component,
      dueAtMinutes: component.lastDoneMinutes + component.intervalHours * MINUTES_PER_HOUR,
      minutesToDue,
      pastDue: minutesToDue <= 0,
    })
  }
  return due
}

export const AircraftComponentInputSchema = z.object({
  kind: z.enum(COMPONENT_KINDS),
  name: z.string().trim().min(1).max(80),
  intervalHours: z.number().int().min(1).max(20000).nullable(),
  lastDoneMinutes: z.number().int().min(0),
  lastDoneOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'A date, YYYY-MM-DD')
    .nullable(),
  notes: z
    .string()
    .trim()
    .max(500)
    .nullish()
    .transform((v) => (v ? v : null)),
  alertBeforeHours: z.number().int().min(0).max(20000).default(50),
  isTbo: z.boolean().default(false),
})
export type AircraftComponentInput = z.infer<typeof AircraftComponentInputSchema>

// GET /api/maintenance/:registration/overview: everything the huolto app's
// aircraft screen shows, in one round trip.
export interface MaintenanceOverview {
  registration: string
  aircraftType: string | null
  airframeMinutes: number | null
  schedule: MaintenanceSchedule | null
  status: MaintenanceStatus | null
  records: MaintenanceRecord[]
  components: (AircraftComponent & { status: ComponentStatus | null; forecast: ComponentForecast | null })[]
}

// When a part is predicted to fall due, from the same booking-based
// estimate as the airframe forecast (apps/api/src/domain/maintenanceForecast.ts).
// null without an interval or an open journey log.
export interface ComponentForecast {
  hoursRemaining: number | null
  outcome: { kind: 'date'; date: string } | { kind: 'exceeded' } | { kind: 'no_estimate' }
}

// POST /api/maintenance/:registration/components/:id/complete: one part
// done on its own, without a maintenance record. Both default to now.
export const ComponentCompleteInputSchema = z.object({
  airframeMinutes: z.number().int().min(0).optional(),
  performedOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'A date, YYYY-MM-DD')
    .optional(),
})
export type ComponentCompleteInput = z.infer<typeof ComponentCompleteInputSchema>

// --- Inputs --------------------------------------------------------------------

const DATE = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'A date, YYYY-MM-DD')
const TYPE = z.string().trim().min(1).max(40)
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null))

export const MaintenanceScheduleInputSchema = z.object({
  cycleHours: z.number().int().min(1).max(10000),
  nextType: TYPE,
  nextDueMinutes: z.number().int().min(0),
  nextDueDate: DATE.nullable(),
  toleranceHours: z.number().int().min(0).max(1000),
  reservedHours: z.number().int().min(0).max(1000),
  notes: optionalText(2000),
})
export type MaintenanceScheduleInput = z.infer<typeof MaintenanceScheduleInputSchema>

// "Maintenance performed": the record, and where the schedule goes next
// (prefilled in the dialog as this maintenance + one cycle).
export const MaintenanceRecordInputSchema = z.object({
  performedOn: DATE,
  maintenanceType: TYPE,
  airframeMinutes: z.number().int().min(0),
  performedBy: optionalText(200),
  notes: optionalText(2000),
  next: MaintenanceScheduleInputSchema.nullable(),
  // Parts overhauled or serviced at this maintenance: their "last done" moves
  // to this record's airframe hours and date, in the same transaction.
  componentsDone: z.array(z.number().int().min(1)).max(50).default([]),
})
export type MaintenanceRecordInput = z.infer<typeof MaintenanceRecordInputSchema>
